import React, { useState } from "react";
import { styled, useTheme } from "@material-ui/styles";
import { Grid, Card, Box, Divider, TextField, IconButton, Chip, Theme } from "@material-ui/core";
import Typography from "../typography";
import { IStyledArguments } from "../../types/styled-arguments";
import EventIcon from "@material-ui/icons/Event";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import PeopleIcon from "@material-ui/icons/People";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";

interface IMatchInfoCard {
  match: any;
  onLocationUpdate?: (location: string) => void;
}

// Card plana — superficie blanca, un único acento, sin glow
const InfoCard = styled(Card)(({ theme }: IStyledArguments) => ({
  borderRadius: "18px",
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.2),
    borderRadius: "16px",
    marginBottom: theme.spacing(2),
  },
}));

const InfoRow = styled(Grid)(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(2),
  "&:last-child": {
    marginBottom: 0,
  },
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(0.5),
  },
}));

const InfoItem = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "14px",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  transition: "border-color 0.2s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    gap: theme.spacing(1),
    borderRadius: "10px",
  },
}));

const IconWrapper = styled(Box)(({ theme }: IStyledArguments) => ({
  width: "48px",
  height: "48px",
  borderRadius: "12px",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  flexShrink: 0,
  "& svg": {
    fontSize: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    "& svg": {
      fontSize: "18px",
    },
  },
}));

const InfoContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
});

const InfoLabel = styled(Typography.Body)(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "1px",
  marginBottom: "4px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.65rem",
    marginBottom: "2px",
  },
}));

const InfoValue = styled(Typography.Body)(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.primary,
  fontSize: "1.05rem",
  fontWeight: 600,
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.85rem",
  },
}));

const SectionTitle = styled(Typography.H6)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: "1.5rem",
  letterSpacing: "-0.5px",
  marginBottom: theme.spacing(2.5),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  "& svg": {
    color: theme.palette.primary.main,
    fontSize: "28px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.1rem",
    marginBottom: theme.spacing(1),
    gap: theme.spacing(0.5),
    "& svg": {
      fontSize: "18px",
    },
  },
}));

const EditableInfoItem = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "14px",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  transition: "border-color 0.2s ease",
  position: "relative",
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
  "&:hover .edit-button": {
    opacity: 1,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    gap: theme.spacing(1),
    borderRadius: "10px",
  },
}));

const EditButton = styled(IconButton)(({ theme }: IStyledArguments) => ({
  position: "absolute",
  top: "8px",
  right: "8px",
  padding: "6px",
  opacity: 0,
  transition: "opacity 0.2s ease, background 0.2s ease",
  background: theme.palette.primary.main,
  color: "white",
  "&:hover": {
    background: theme.palette.primary.dark,
  },
  "& svg": {
    fontSize: "18px",
  },
}));

const ActionButton = styled(IconButton)(({ theme }: IStyledArguments) => ({
  padding: "4px",
  marginLeft: theme.spacing(0.5),
  "& svg": {
    fontSize: "18px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }: IStyledArguments) => ({
  "& .MuiInputBase-root": {
    fontSize: "1rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  "& .MuiInput-underline:before": {
    borderBottomColor: theme.palette.primary.main,
  },
  "& .MuiInput-underline:hover:before": {
    borderBottomColor: theme.palette.primary.dark,
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: theme.palette.primary.main,
  },
}));

// Media section for videos and photos
const MediaSection = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1),
    marginTop: 0,
  },
}));

const StyledDivider = styled(Divider)(({ theme }: IStyledArguments) => ({
  margin: "20px 0",
  [theme.breakpoints.down("sm")]: {
    margin: "8px 0",
  },
}));

const CompactGrid = styled(Grid)(({ theme }: IStyledArguments) => ({
  [theme.breakpoints.down("sm")]: {
    "& .MuiGrid-item": {
      paddingTop: "4px",
      paddingBottom: "4px",
    },
  },
}));

const MediaTitle = styled(Typography.Body)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.text.secondary,
  fontSize: "0.85rem",
  textTransform: "uppercase",
  letterSpacing: "1px",
  marginBottom: "12px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.7rem",
    marginBottom: "4px",
  },
}));

const MediaChip = styled(Chip)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.accentSoft,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: "12px",
  padding: theme.spacing(1.5, 1),
  fontWeight: 600,
  fontSize: "0.9rem",
  transition: "border-color 0.2s ease",
  "& .MuiChip-icon": {
    color: theme.palette.primary.main,
    fontSize: "20px",
  },
  "& .MuiChip-label": {
    padding: "0 8px",
    fontWeight: 600,
  },
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: theme.spacing(0.8, 0.5),
    borderRadius: "8px",
    "& .MuiChip-icon": {
      fontSize: "16px",
    },
    "& .MuiChip-label": {
      padding: "0 6px",
    },
  },
}));

const MatchInfoCard = ({ match, onLocationUpdate }: IMatchInfoCard) => {
  const theme = useTheme<Theme>();
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [locationValue, setLocationValue] = useState(match?.location || "");

  if (!match) return null;

  // Get media counts
  const videosCount = match.videos?.length || 0;
  const photosCount = match.photos?.length || 0;
  const hasMedia = videosCount > 0 || photosCount > 0;

  const handleStartEdit = () => {
    setLocationValue(match.location || "");
    setIsEditingLocation(true);
  };

  const handleSaveLocation = () => {
    if (onLocationUpdate && locationValue.trim()) {
      onLocationUpdate(locationValue.trim());
    }
    setIsEditingLocation(false);
  };

  const handleCancelEdit = () => {
    setLocationValue(match.location || "");
    setIsEditingLocation(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveLocation();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  return (
    <InfoCard>
      <SectionTitle>
        <EventIcon />
        Match Information
      </SectionTitle>
      
      <StyledDivider />

      <CompactGrid container spacing={2}>
        {/* Date */}
        <InfoRow item xs={12} sm={6} md={3}>
          <InfoItem>
            <IconWrapper>
              <EventIcon />
            </IconWrapper>
            <InfoContent>
              <InfoLabel>Date</InfoLabel>
              <InfoValue>{match.date}</InfoValue>
            </InfoContent>
          </InfoItem>
        </InfoRow>

        {/* Time */}
        <InfoRow item xs={12} sm={6} md={3}>
          <InfoItem>
            <IconWrapper>
              <AccessTimeIcon />
            </IconWrapper>
            <InfoContent>
              <InfoLabel>Time</InfoLabel>
              <InfoValue>{match.time}</InfoValue>
            </InfoContent>
          </InfoItem>
        </InfoRow>

        {/* Location - Editable */}
        <InfoRow item xs={12} sm={6} md={3}>
          <EditableInfoItem>
            <IconWrapper>
              <LocationOnIcon />
            </IconWrapper>
            <InfoContent>
              <InfoLabel>Location</InfoLabel>
              {!isEditingLocation ? (
                <InfoValue>{match.location || "To be defined"}</InfoValue>
              ) : (
                <Box display="flex" alignItems="center" style={{ width: "100%" }}>
                  <StyledTextField
                    value={locationValue}
                    onChange={(e) => setLocationValue(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Enter location"
                    autoFocus
                    fullWidth
                    size="small"
                  />
                  <ActionButton
                    onClick={handleSaveLocation}
                    style={{ color: theme.palette.extra.success }}
                    size="small"
                  >
                    <CheckIcon />
                  </ActionButton>
                  <ActionButton
                    onClick={handleCancelEdit}
                    style={{ color: theme.palette.error.main }}
                    size="small"
                  >
                    <CloseIcon />
                  </ActionButton>
                </Box>
              )}
            </InfoContent>
            {!isEditingLocation && (
              <EditButton
                className="edit-button"
                onClick={handleStartEdit}
                size="small"
              >
                <EditIcon />
              </EditButton>
            )}
          </EditableInfoItem>
        </InfoRow>

        {/* Players Confirmed */}
        <InfoRow item xs={12} sm={6} md={3}>
          <InfoItem>
            <IconWrapper>
              <PeopleIcon />
            </IconWrapper>
            <InfoContent>
              <InfoLabel>Players</InfoLabel>
              <InfoValue>
                {match.playersConfirmed?.length || 0} confirmed
              </InfoValue>
            </InfoContent>
          </InfoItem>
        </InfoRow>
      </CompactGrid>

      {/* Additional Info if available */}
      {(match.league || match.homeTeam?.league) && (
        <>
          <StyledDivider />
          <Box display="flex" alignItems="center" style={{ gap: "6px" }}>
            <Typography.Body style={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}>
              Liga:
            </Typography.Body>
            <Typography.Body style={{ color: theme.palette.text.primary, fontWeight: 600, fontSize: "0.85rem" }}>
              {match.league?.name || match.homeTeam?.league?.name || "N/A"}
            </Typography.Body>
          </Box>
        </>
      )}

      {/* Media Section - Videos & Photos */}
      {hasMedia && (
        <>
          <StyledDivider />
          <Box>
            <MediaTitle>
              Match Media
            </MediaTitle>
            <MediaSection>
              {videosCount > 0 && (
                <MediaChip
                  icon={<VideoLibraryIcon />}
                  label={`${videosCount} Video${videosCount !== 1 ? 's' : ''}`}
                  clickable
                />
              )}
              {photosCount > 0 && (
                <MediaChip
                  icon={<PhotoLibraryIcon />}
                  label={`${photosCount} Photo${photosCount !== 1 ? 's' : ''}`}
                  clickable
                />
              )}
            </MediaSection>
          </Box>
        </>
      )}
    </InfoCard>
  );
};

export default MatchInfoCard;
